import React from 'react';
import {
    Paper,
    Typography,
    Box,
    List,
    Divider,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { useNotifications } from '../context/NotificationContext';
import NotificationItem from './NotificationItem';

/**
 * Styled components for the dropdown
 */
const DropdownPaper = styled(Paper)(({ theme }) => ({
    position: 'absolute',
    top: '100%',
    right: 0,
    width: 420,
    maxHeight: 600,
    zIndex: 1300,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    borderRadius: 12,
    border: `1px solid ${theme.palette.grey[200]}`,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        width: 'calc(100vw - 32px)',
        right: -16,
        maxHeight: 'calc(100vh - 120px)',
    },
}));

const Header = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    backgroundColor: theme.palette.background.paper,
}));

const ContentContainer = styled(Box)({
    maxHeight: 400,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
        width: 6,
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#E0E7FF',
        borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: '#C7D2FE',
    },
});

const Footer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.grey[100]}`,
    backgroundColor: theme.palette.grey[50],
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 3),
    color: theme.palette.grey[600],
}));

/**
 * NotificationDropdown Component
 * Large dropdown menu that appears below the notification bell
 */
const NotificationDropdown = ({ open, anchorEl, onClose }) => {
    const {
        sortedNotifications,
        unreadCount,
        markAsViewed,
        markAllAsViewed,
        loadMoreNotifications,
        isLoading,
        isLoadingMore,
        isMarkingAsRead,
        hasNext,
        error,
    } = useNotifications();

    if (!open) return null;

    const handleMarkAllAsRead = () => {
        markAllAsViewed();
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <EmptyState>
                    <CircularProgress size={32} sx={{ mb: 2 }} />
                    <Typography variant="body2">Loading notifications...</Typography>
                </EmptyState>
            );
        }

        if (error) {
            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="error" variant="outlined">
                        {error}
                    </Alert>
                </Box>
            );
        }

        if (!sortedNotifications || sortedNotifications.length === 0) {
            return (
                <EmptyState>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                        No notifications
                    </Typography>
                    <Typography variant="body2">You&apos;re all caught up!</Typography>
                </EmptyState>
            );
        }

        return (
            <>
                <List sx={{ p: 0 }}>
                    {sortedNotifications.map((notification, index) => (
                        <React.Fragment key={notification.notificationId}>
                            <NotificationItem
                                notification={notification}
                                onMarkAsRead={markAsViewed}
                                onClose={onClose}
                            />
                            {index < sortedNotifications.length - 1 && (
                                <Divider variant="inset" component="li" />
                            )}
                        </React.Fragment>
                    ))}
                </List>

                {/* See More Button */}
                {hasNext && (
                    <Box sx={{ p: 2, borderTop: 1, borderColor: 'grey.100' }}>
                        <Button
                            fullWidth
                            variant="text"
                            size="small"
                            onClick={loadMoreNotifications}
                            disabled={isLoadingMore}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                color: 'primary.main',
                                '&:hover': {
                                    backgroundColor: 'primary.light',
                                },
                            }}
                        >
                            {isLoadingMore ? (
                                <>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    Loading...
                                </>
                            ) : (
                                'See More'
                            )}
                        </Button>
                    </Box>
                )}
            </>
        );
    };

    const hasUnread = sortedNotifications?.some((n) => !n.viewed) || false;

    return (
        <DropdownPaper>
            {/* Header */}
            <Header>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Notifications
                </Typography>
                {unreadCount > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {unreadCount} unread
                    </Typography>
                )}
            </Header>

            {/* Content */}
            <ContentContainer>{renderContent()}</ContentContainer>

            {/* Footer */}
            {hasUnread && (
                <Footer>
                    <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={handleMarkAllAsRead}
                        disabled={isMarkingAsRead}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                                borderColor: 'primary.dark',
                                backgroundColor: 'primary.light',
                            },
                        }}
                    >
                        {isMarkingAsRead ? 'Marking as read...' : 'Mark all as read'}
                    </Button>
                </Footer>
            )}
        </DropdownPaper>
    );
};

export default NotificationDropdown;
